import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import Toast from '../components/Toast';
import googleAuthService from '../services/googleAuthService';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import PasswordValidator from '../components/PasswordValidator';
import { useMetaPixel } from '../hooks/use-meta-pixel';
import { useGoogleAnalytics } from '../hooks/use-google-analytics';
import { useGoogleAds } from '../hooks/use-google-ads';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const { register } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { trackRegistration } = useMetaPixel();
  const { trackRegistration: trackGARegistration } = useGoogleAnalytics();
  const { trackRegistration: trackAdsRegistration } = useGoogleAds();

  useEffect(() => {
    // Initialize Google Sign-In when component mounts
    const initGoogleSignIn = async () => {
      try {
        const googleButton = document.getElementById('google-signin-button');
        
        // If button container not found, skip
        if (!googleButton) {
          console.error('Google Sign-In button container not found in DOM');
          return;
        }
        
        console.log('Initializing Google Sign-In for registration...');
        
        // Call Google Sign-In with registration flag (true)
        const response = await googleAuthService.handleGoogleSignIn(true);
        
        console.log('Google Sign-In registration response:', response);
        
        // Check if we have a successful registration/login
        if (response && response.success && response.token) {
          // Store the token securely
          localStorage.setItem('token', response.token);
          
          // Add any refresh token if available
          if (response.refreshToken) {
            localStorage.setItem('refreshToken', response.refreshToken);
          }
          
          // Track successful Google registration with Meta Pixel
          trackRegistration({
            content_name: 'google_registration',
            content_category: 'registration',
            user_data: {
              email: response.user?.email || '',
              name: response.user?.name || ''
            }
          });

          // Track successful Google registration with Google Analytics
          trackGARegistration({
            method: 'google',
            user_data: {
              email: response.user?.email || '',
              name: response.user?.name || ''
            }
          });

          // Track successful Google registration with Google Ads
          trackAdsRegistration({
            method: 'google',
            value: 10, // Assign value to registration conversion
            currency: 'USD',
            user_data: {
              email: response.user?.email || '',
              name: response.user?.name || ''
            }
          });
          
          // Show success toast
          showToast('Google account registration successful! Redirecting to dashboard...', 'success');
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        }
      } catch (error) {
        console.error('Google Sign-In error:', error);
        
        // Extract specific error information
        let errorMessage = 'Google Sign-In failed. Please try again.';
        console.error('Error details:', error);
        if (error.code === 'ACCOUNT_EXISTS') {
          errorMessage = 'This Google account is already registered. Redirecting to login...';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Show error toast
        showToast(errorMessage, 'error');

        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    };

    // Slight delay for DOM to be ready
    setTimeout(() => {
      initGoogleSignIn();
    }, 250);

    // Cleanup when component unmounts
    return () => {
      googleAuthService.cleanup();
    };
  }, [navigate, trackRegistration, trackGARegistration, trackAdsRegistration]); // Add trackAdsRegistration to dependencies

  const handleToastClose = () => {
    setToast({ ...toast, open: false });
  };

  const showToast = (message, severity = 'error') => {
    setToast({ open: true, message, severity });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        authProvider: 'email'
      };
      
      const result = await register(userData);
      
      if (result && result.success) {
        // Track successful registration with Meta Pixel
        trackRegistration({
          content_name: 'email_registration',
          content_category: 'registration',
          user_data: {
            email: formData.email,
            name: formData.name
          }
        });

        // Track successful registration with Google Analytics
        trackGARegistration({
          method: 'email',
          user_data: {
            email: formData.email,
            name: formData.name
          }
        });

        // Track successful registration with Google Ads
        trackAdsRegistration({
          method: 'email',
          value: 10, // Assign value to registration conversion
          currency: 'USD',
          user_data: {
            email: formData.email,
            name: formData.name
          }
        });
        
        showToast('Registration successful! Please check your email to verify your account.', 'success');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Registration successful! Please check your email to verify your account before logging in.',
              requireVerification: true
            }
          });
        }, 3000);
      }
    } catch (err) {
      //console.error('Registration error:', err);

      console.log('Registration error:', err);
      
      // Extract error details from various possible formats
      let errorCode = '';
      let errorMessage = '';

      // Better error logging with full object details
      //console.log('Error object:', JSON.stringify(err, Object.getOwnPropertyNames(err), 2));
      
      // Handle structured API error response
      if (err.data) {
        errorCode = err.data.code;
        errorMessage = err.data.message;
        
        // Check if we should redirect to login
        const shouldLogin = err.data.shouldLogin;
        const provider = err.data.provider;
        
        if (shouldLogin) {
          showToast(errorMessage || 'Email is already registered. Please login instead.', 'info');
          
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                email: formData.email,
                message: `This email is already registered with ${provider || 'an account'}. Please sign in.`
              }
            });
          }, 2000);
          return;
        }
      } 
      // Handle JSON string error
      else if (typeof err.message === 'string' && err.message.startsWith('{')) {
        try {
          const parsedError = JSON.parse(err.message);
          errorCode = parsedError.code;
          errorMessage = parsedError.message;
        } catch (parseError) {
          console.log('Failed to parse error JSON:', parseError);
        }
      } 
      // Handle direct error object
      else if (err.code && err.message) {
        errorCode = err.code;
        errorMessage = err.message;
      } 
      // Handle plain object with success property
      else if (typeof err === 'object' && err !== null && err.success === false) {
        errorCode = err.code || '';
        errorMessage = err.message || 'Registration failed';
      }
      
      console.log('Processed registration error:', { code: errorCode, message: errorMessage });
      
      // Handle specific error codes based on your backend error codes
      switch (errorCode) {
        case 'EMAIL_ALREADY_EXISTS':
          showToast(errorMessage || 'An account with this email already exists. Please sign in instead.', 'info');
          
          setTimeout(() => {
            navigate('/login', { 
              state: { email: formData.email }
            });
          }, 2000);
          break;
          
        case 'INVALID_CREDENTIALS':
          showToast(errorMessage || 'Please fill in all required fields.', 'error');
          break;
          
        case 'INVALID_EMAIL':
          showToast(errorMessage || 'Please provide a valid email address.', 'error');
          setFormErrors(prev => ({ 
            ...prev, 
            email: errorMessage || 'Please provide a valid email address.'
          }));
          break;
          
        case 'INVALID_PASSWORD':
          showToast(errorMessage || 'Password must be at least 8 characters long.', 'error');
          setFormErrors(prev => ({ 
            ...prev, 
            password: errorMessage || 'Password must be at least 8 characters long.'
          }));
          break;
          
        case 'NETWORK_ERROR':
          showToast(errorMessage || 'Network error. Please check your connection and try again.', 'error');
          break;
          
        default:
          // If we have an error message, use it
          if (errorMessage) {
            showToast(errorMessage, 'error');
          } else {
            // Generic fallback
            showToast('Registration failed. Please try again.', 'error');
          }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Register | Gravity Point Media" 
        description="Create a new account with Gravity Point Media to access our website support and maintenance services." 
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 6 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                Create Account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Join us to get started with your website support
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <div id="google-signin-button" 
              style={{ 
                  width: '100%', 
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: '50px'
                }}
                ></div>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  or
                </Typography>
              </Divider>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={formData.name}
                onChange={handleChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Email address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <PasswordValidator password={formData.password} />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                sx={{ mb: 4 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(45deg, #90caf9, #bbdefb)',
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <Box mt={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  to="/login"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Sign in here
                </Link>
              </Typography>
            </Box>

            <Box mt={3} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                By signing up, you agree to our{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleToastClose}
        autoHideDuration={toast.severity === 'success' ? 2000 : 6000} // Shorter for success
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Better visibility
      />
    </>
  );
};

export default Register;